import type { AssetManifest, BossAbilityKind } from '@shefira/shared';
import type Phaser from 'phaser';

import { loadLazyAtlases } from '../../assets/manifest-loader.js';
import { DRAGON_CLIPS } from '../cinematics/dragon-clips.js';
import { shadowTexture, silhouetteTexture } from '../cinematics/shadow-texture.js';
import { onHerBody, type Point, SHEFIRA_ART_CLIPS, ShefiraMotion } from './shefira-motion.js';

/**
 * Shefira drawn in her Aire (ADR 0068): her 256×224 canvas laid by its feet
 * on her tile in whole pixels, the frame her motion says, her shadow on the
 * ground while she flies. Her clips are the lazy atlases of her scenes,
 * fetched when the Aire opens; until they come (or if they never do), the
 * stand-in body the bestiary gives her stays on the ground, and in the sky
 * the silhouette drawn by code is her shadow. Only a view: every clip is
 * chosen from what the server said.
 */

/** Over every standing sprite and plate, under the cinematics (999 000) and the veil. */
const SKY_DEPTH = 900_000;
/** On the ground, over the shapes' warnings (-390), under every standing sprite. */
const SHADOW_DEPTH = -380;

type Sheets = AssetManifest['sheets'];

const ART = [...new Set(Object.values(SHEFIRA_ART_CLIPS).map((clip) => clip.art))];

export class ShefiraView {
  private readonly dragon: Phaser.GameObjects.Image;
  private readonly shadow: Phaser.GameObjects.Image;
  private motion: ShefiraMotion | undefined;
  private art: 'loading' | 'ready' | 'missing' = 'loading';
  /** Her feet as last drawn, for a click on her body. */
  private feet: Point | undefined;
  private shown = false;
  /** What the server last said, for a motion begun after it (her entity came in late). */
  private airborneNow = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly manifest: Pick<AssetManifest, 'atlases' | 'sheets'>,
  ) {
    this.dragon = scene.add.image(0, 0, silhouetteTexture(scene)).setOrigin(0, 0).setVisible(false);
    this.shadow = scene.add
      .image(0, 0, silhouetteTexture(scene))
      .setOrigin(0, 0)
      .setDepth(SHADOW_DEPTH)
      .setVisible(false);
    void this.load();
  }

  private get sheets(): Sheets {
    return this.manifest.sheets;
  }

  private async load(): Promise<void> {
    const atlases = ART.map((art) => this.sheets[art]?.atlas).filter(
      (atlas): atlas is string => atlas !== undefined,
    );
    if (atlases.length < ART.length) {
      this.art = 'missing';
      return;
    }
    const loaded = await loadLazyAtlases(this.scene, this.manifest, atlases);
    this.art = atlases.every((atlas) => loaded.has(atlas)) ? 'ready' : 'missing';
  }

  /** Her motion, begun where she stands; kept for the room's life. */
  private motionAt(feet: Point): ShefiraMotion {
    if (this.motion === undefined) {
      this.motion = new ShefiraMotion(feet);
      if (this.airborneNow) this.motion.sync(true, this.scene.time.now);
    }
    return this.motion;
  }

  get airborne(): boolean {
    return this.motion?.airborne === true;
  }

  /** She flees at the story's end: how long before she is gone, for her flight's scene to wait. */
  fleeLeft(now: number): number {
    return this.motion?.fleeLeft(now) ?? 0;
  }

  sync(airborne: boolean, now: number): void {
    this.airborneNow = airborne;
    this.motion?.sync(airborne, now);
  }

  takeoff(ms: number, now: number): void {
    this.motion?.takeoff(ms, now);
  }

  landing(ms: number, now: number): void {
    this.motion?.landing(ms, now);
  }

  shape(kind: BossAbilityKind, ms: number, now: number): void {
    this.motion?.shape(kind, ms, now);
  }

  ring(ms: number, now: number): void {
    this.motion?.ring(ms, now);
  }

  dive(centre: Point, ms: number, now: number): void {
    this.motion?.dive(centre, ms, now);
  }

  sword(now: number): void {
    this.motion?.sword(now);
  }

  /** The middle of her body as last drawn, in the sky or on the ground: where a harpoon flies. */
  get aim(): Point | undefined {
    return this.shown && this.feet !== undefined
      ? { x: this.feet.x, y: this.feet.y - 60 }
      : undefined;
  }

  /** Whether a click at this world point is on her, as she is drawn on the ground. */
  contains(point: Point): boolean {
    return this.shown && !this.airborne && this.feet !== undefined && onHerBody(this.feet, point);
  }

  /**
   * One frame: `feet` where the scene draws her tile (undefined once she has
   * left the room), `alive` false once slain; `standIn` is her bestiary body,
   * shown only while her own art is not there.
   */
  update(
    now: number,
    feet: Point | undefined,
    alive: boolean,
    standIn: { container: Phaser.GameObjects.Container } | undefined,
  ): void {
    if (feet === undefined && this.motion === undefined) {
      this.hide();
      return;
    }
    const motion = this.motionAt(feet ?? { x: 0, y: 0 });
    if (feet !== undefined) motion.setAnchor(feet, now);
    if (!alive) motion.fall(now);
    const pose = motion.pose(now);
    const ready = this.art === 'ready';
    standIn?.container.setVisible(!ready && !pose.high && !pose.hidden);
    if (pose.hidden || (feet === undefined && !motion.ending)) {
      this.hide();
      return;
    }
    this.shown = true;
    this.feet = pose.feet;
    const clip = SHEFIRA_ART_CLIPS[pose.clip];
    const sheet = this.sheets[clip.art];
    if (ready && sheet !== undefined) {
      this.dragon
        .setTexture(sheet.atlas, `${sheet.prefix}/${String(pose.frame)}`)
        .setPosition(pose.feet.x - clip.feet.x, pose.feet.y - clip.feet.y)
        .setDepth(pose.high ? SKY_DEPTH : pose.feet.y)
        .setAlpha(pose.alpha)
        .setVisible(true);
    } else {
      this.dragon.setVisible(false);
    }
    this.drawShadow(pose.shadow, now, ready);
  }

  private drawShadow(
    shadow: { at: Point; scale: number; alpha: number } | null,
    now: number,
    ready: boolean,
  ): void {
    if (shadow === null) {
      this.shadow.setVisible(false);
      return;
    }
    const sheet = this.sheets[DRAGON_CLIPS.shadow.art];
    const frame = Math.floor(now / DRAGON_CLIPS.shadow.ms) % DRAGON_CLIPS.shadow.frames;
    const texture =
      ready && sheet !== undefined
        ? shadowTexture(this.scene, sheet, frame, shadow.scale)
        : undefined;
    if (texture !== undefined) {
      const { feet } = DRAGON_CLIPS.shadow;
      if (this.shadow.texture.key !== texture) this.shadow.setTexture(texture);
      this.shadow
        .setScale(1)
        .setAlpha(shadow.alpha)
        .setPosition(
          shadow.at.x - Math.round(feet.x * shadow.scale),
          shadow.at.y - Math.round(feet.y * shadow.scale),
        )
        .setVisible(true);
      return;
    }
    // The stand-in, at a whole magnification, centred on the same ground point.
    const magnify = Math.max(1, Math.round(shadow.scale * 3));
    this.shadow.setTexture(silhouetteTexture(this.scene)).setScale(magnify).setAlpha(shadow.alpha);
    this.shadow
      .setPosition(
        shadow.at.x - Math.round((this.shadow.width * magnify) / 2),
        shadow.at.y - Math.round((this.shadow.height * magnify) / 2),
      )
      .setVisible(true);
  }

  private hide(): void {
    this.shown = false;
    this.dragon.setVisible(false);
    this.shadow.setVisible(false);
  }

  destroy(): void {
    this.dragon.destroy();
    this.shadow.destroy();
  }
}
